import { useSyncExternalStore } from 'react';

/**
 * Who is recording which call for an AI summary (told by realtime `call:recording` events), and
 * whether this device is the one recording.
 */
type State = { mine: string | null; others: Map<string, Map<string, string>> };
let state: State = { mine: null, others: new Map() };
const listeners = new Set<() => void>();
const emit = (next: State) => {
  state = next;
  for (const listener of listeners) listener();
};

export const recordingStore = {
  subscribe: (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  setMine(channelId: string | null) {
    emit({ ...state, mine: channelId });
  },
  /** From the realtime event: someone (maybe on another device) started or stopped. */
  remote(event: { channelId: string; userId: string; displayName: string; on: boolean }) {
    const others = new Map(state.others);
    const people = new Map(others.get(event.channelId));
    if (event.on) people.set(event.userId, event.displayName);
    else people.delete(event.userId);
    others.set(event.channelId, people);
    emit({ ...state, others });
  },
  mine: () => state.mine,
};

export const useRecordingState = () => useSyncExternalStore(recordingStore.subscribe, () => state);

/**
 * Mixes the microphone and everyone else's audio into one compressed track (Opus, ~32 kbps:
 * an hour is ~14 MB) while the call goes on. Tracks that join later are added as they arrive.
 */
export class CallMixRecorder {
  private readonly context = new AudioContext();
  private readonly destination = this.context.createMediaStreamDestination();
  private readonly sources = new Map<string, MediaStreamAudioSourceNode>();
  private readonly chunks: Blob[] = [];
  private readonly recorder: MediaRecorder;

  constructor() {
    const mimeType = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/webm'].find((m) =>
      MediaRecorder.isTypeSupported(m),
    );
    this.recorder = new MediaRecorder(this.destination.stream, {
      ...(mimeType ? { mimeType } : {}),
      audioBitsPerSecond: 32_000,
    });
    this.recorder.ondataavailable = (event) => {
      if (event.data.size > 0) this.chunks.push(event.data);
    };
  }

  start() {
    void this.context.resume();
    this.recorder.start(10_000);
  }

  /** Adds (or keeps) the audio of a track; `id` avoids adding the same one twice. */
  add(id: string, track: MediaStreamTrack) {
    if (this.sources.has(id) || track.kind !== 'audio') return;
    const source = this.context.createMediaStreamSource(new MediaStream([track]));
    source.connect(this.destination);
    this.sources.set(id, source);
  }

  remove(id: string) {
    this.sources.get(id)?.disconnect();
    this.sources.delete(id);
  }

  /** Stops and returns the recording. */
  stop(): Promise<File> {
    return new Promise((resolve) => {
      this.recorder.onstop = () => {
        const type = this.recorder.mimeType || 'audio/webm';
        resolve(new File(this.chunks, `call.${type.includes('ogg') ? 'ogg' : 'webm'}`, { type }));
        for (const source of this.sources.values()) source.disconnect();
        void this.context.close();
      };
      if (this.recorder.state === 'inactive') this.recorder.onstop(new Event('stop'));
      else this.recorder.stop();
    });
  }
}
